import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import type { Stroke, StrokeInput, Point, ChannelConfig, AnimatedDrawing } from '@twitch-draw/shared';
import { calculateSubmissionBits, DEFAULT_CHANNEL_CONFIG, snapBitsToSkuTier } from '@twitch-draw/shared';

const MAX_FRAMES = 8;
const MAX_FPS = 4;
const MAX_HOLD = 4;

const getTierAmount = (rawAmount: number): number => snapBitsToSkuTier(rawAmount);

export type AppState = 'loading' | 'drawing' | 'confirming' | 'processing';

export interface TimelineFrame {
  id: string;
  strokes: StrokeInput[];
  hold: number;
}

export interface OnionSkinData {
  frames: StrokeInput[][];
  activeIndex: number;
  isPreviewing: boolean;
}

let frameIdCounter = 0;
const generateFrameId = () => `frame-${++frameIdCounter}-${Date.now()}`;

interface UseDrawingStateOptions {
  initialStrokes?: Stroke[];
  channelId?: string;
  userId?: string;
  channelConfig?: ChannelConfig | null;
  viewerDisplayName?: string;
}

export function useDrawingState(options: UseDrawingStateOptions = {}) {
  const {
    initialStrokes = [],
    channelId = 'unknown',
    userId = 'unknown',
    channelConfig,
    viewerDisplayName = 'You',
  } = options;
  const config = channelConfig ?? DEFAULT_CHANNEL_CONFIG;
  const pendingDisplayName = viewerDisplayName.trim() || 'You';

  const [appState, setAppState] = useState<AppState>('drawing');
  const [strokes, setStrokes] = useState<Stroke[]>(initialStrokes);
  const [pendingStrokes, setPendingStrokes] = useState<StrokeInput[]>([]);
  const [currentStroke, setCurrentStroke] = useState<StrokeInput | null>(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [color, setColor] = useState('#ff0000');
  const [width, setWidth] = useState(6);
  const [isEraser, setIsEraser] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const displayDuration = config.displayDurationMs;

  const [isAnimationMode, setIsAnimationMode] = useState(false);
  const [timelineFrames, setTimelineFrames] = useState<TimelineFrame[]>([]);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [animationFps, setAnimationFps] = useState(2);
  const [isPreviewingAnimation, setIsPreviewingAnimation] = useState(false);
  const [previewTick, setPreviewTick] = useState(0);

  const startTimeRef = useRef<number>(0);
  const pointsRef = useRef<Point[]>([]);
  const pendingStrokesRef = useRef<StrokeInput[]>([]);

  useEffect(() => {
    if (timelineFrames.length === 0 && currentFrameIndex !== 0) setCurrentFrameIndex(0);
    if (timelineFrames.length > 0 && currentFrameIndex >= timelineFrames.length) {
      setCurrentFrameIndex(timelineFrames.length - 1);
    }
  }, [timelineFrames.length, currentFrameIndex]);

  const effectivePendingStrokes = useMemo(
    () => (isAnimationMode ? timelineFrames.flatMap((f) => f.strokes) : pendingStrokes),
    [isAnimationMode, timelineFrames, pendingStrokes],
  );

  const submissionAnimation = useMemo<AnimatedDrawing | undefined>(() => {
    if (!isAnimationMode || timelineFrames.length <= 1) return undefined;
    const expandedFrames = timelineFrames.flatMap((frame) =>
      Array.from({ length: frame.hold }, () => frame.strokes),
    );
    if (expandedFrames.length <= 1) return undefined;
    return { frames: expandedFrames, fps: animationFps };
  }, [isAnimationMode, timelineFrames, animationFps]);

  pendingStrokesRef.current = effectivePendingStrokes;

  const previewFrameIndex = useMemo(() => {
    if (!isPreviewingAnimation || timelineFrames.length === 0) return currentFrameIndex;
    const totalTicks = timelineFrames.reduce((sum, f) => sum + f.hold, 0);
    if (totalTicks === 0) return 0;
    const tick = previewTick % totalTicks;
    let cumulative = 0;
    const idx = timelineFrames.findIndex((f) => {
      cumulative += f.hold;
      return tick < cumulative;
    });
    return idx >= 0 ? idx : timelineFrames.length - 1;
  }, [isPreviewingAnimation, previewTick, timelineFrames, currentFrameIndex]);

  const onionSkinData = useMemo((): OnionSkinData | null => {
    if (!isAnimationMode || timelineFrames.length === 0) return null;
    return {
      frames: timelineFrames.map((f) => f.strokes),
      activeIndex: isPreviewingAnimation ? previewFrameIndex : currentFrameIndex,
      isPreviewing: isPreviewingAnimation,
    };
  }, [isAnimationMode, timelineFrames, currentFrameIndex, isPreviewingAnimation, previewFrameIndex]);

  const eraseFromStrokes = useCallback(
    (strokeList: StrokeInput[], point: Point): { strokes: StrokeInput[]; changed: boolean } => {
      const threshold = 0.025;
      let changed = false;
      const isNearPoint = (p: Point) =>
        Math.sqrt((p[0] - point[0]) ** 2 + (p[1] - point[1]) ** 2) < threshold;

      const newStrokes = strokeList.flatMap((stroke) => {
        if (!stroke.points.some(isNearPoint)) return [stroke];
        changed = true;
        const segments: Point[][] = [[]];
        stroke.points.forEach((p) => {
          if (isNearPoint(p)) segments.push([]);
          else segments[segments.length - 1].push(p);
        });
        return segments
          .filter((seg) => seg.length >= 2)
          .map((seg) => ({ ...stroke, points: seg }));
      });

      return { strokes: changed ? newStrokes : strokeList, changed };
    },
    [],
  );

  const handleEraseAtPoint = useCallback(
    (point: Point) => {
      if (!isAnimationMode) {
        setPendingStrokes((prev) => {
          const result = eraseFromStrokes(prev, point);
          return result.changed ? result.strokes : prev;
        });
        return;
      }
      setTimelineFrames((prev) => {
        if (prev.length === 0 || currentFrameIndex >= prev.length) return prev;
        const result = eraseFromStrokes(prev[currentFrameIndex].strokes, point);
        if (!result.changed) return prev;
        return prev.map((f, i) =>
          i === currentFrameIndex ? { ...f, strokes: result.strokes } : f,
        );
      });
    },
    [isAnimationMode, currentFrameIndex, eraseFromStrokes],
  );

  const handleStartStroke = useCallback(
    (point: Point) => {
      if (isPreviewingAnimation) {
        setIsPreviewingAnimation(false);
        setPreviewTick(0);
      }
      if (isEraser) return handleEraseAtPoint(point);
      if (isAnimationMode && timelineFrames.length === 0) return;
      startTimeRef.current = Date.now();
      pointsRef.current = [point];
      setCurrentStroke({ color, width, durationMs: 0, points: [point] });
    },
    [color, width, isEraser, handleEraseAtPoint, isAnimationMode, timelineFrames.length, isPreviewingAnimation],
  );

  const handleContinueStroke = useCallback(
    (point: Point) => {
      if (isEraser) return handleEraseAtPoint(point);
      if (!startTimeRef.current) return;
      pointsRef.current.push(point);
      setCurrentStroke({ color, width, durationMs: Date.now() - startTimeRef.current, points: [...pointsRef.current] });
    },
    [color, width, isEraser, handleEraseAtPoint],
  );

  const handleEndStroke = useCallback(() => {
    if (!startTimeRef.current || pointsRef.current.length < 2) {
      startTimeRef.current = 0;
      pointsRef.current = [];
      setCurrentStroke(null);
      return;
    }

    const stroke: StrokeInput = {
      color,
      width,
      durationMs: Date.now() - startTimeRef.current,
      points: [...pointsRef.current],
    };

    if (isAnimationMode) {
      setTimelineFrames((prev) => {
        if (prev.length === 0 || currentFrameIndex >= prev.length) return prev;
        return prev.map((f, i) =>
          i === currentFrameIndex ? { ...f, strokes: [...f.strokes, stroke] } : f,
        );
      });
    } else {
      setPendingStrokes((prev) => [...prev, stroke]);
    }

    startTimeRef.current = 0;
    pointsRef.current = [];
    setCurrentStroke(null);
  }, [color, width, isAnimationMode, currentFrameIndex]);

  const handleSubmit = useCallback(() => {
    if (effectivePendingStrokes.length === 0) {
      return;
    }
    const rawTotal = calculateSubmissionBits(effectivePendingStrokes, submissionAnimation, 1920, 1080, config);
    setTotalAmount(getTierAmount(rawTotal));
    setAppState('confirming');
  }, [effectivePendingStrokes, submissionAnimation, config]);

  const handleCancelTransaction = useCallback(() => {
    setTotalAmount(0);
    setAppState('drawing');
  }, []);

  const handleClearPending = useCallback(() => {
    if (isAnimationMode) {
      setTimelineFrames([{ id: generateFrameId(), strokes: [], hold: 1 }]);
      setCurrentFrameIndex(0);
    } else {
      setPendingStrokes([]);
    }
  }, [isAnimationMode]);

  const handleToggleAnimationMode = useCallback(() => {
    if (isAnimationMode) {
      setTimelineFrames([]);
      setPendingStrokes([]);
      setCurrentFrameIndex(0);
      setIsPreviewingAnimation(false);
      setPreviewTick(0);
      setIsAnimationMode(false);
      return;
    }
    const currentPending = pendingStrokesRef.current;
    setTimelineFrames([{
      id: generateFrameId(),
      strokes: currentPending.length > 0 ? [...currentPending] : [],
      hold: 1,
    }]);
    setPendingStrokes([]);
    setCurrentFrameIndex(0);
    setIsAnimationMode(true);
  }, [isAnimationMode]);

  const handleAddFrame = useCallback(() => {
    setTimelineFrames((prev) => {
      if (prev.length >= MAX_FRAMES) return prev;
      const updated = [...prev, { id: generateFrameId(), strokes: [], hold: 1 }];
      setCurrentFrameIndex(updated.length - 1);
      return updated;
    });
  }, []);

  const handleDuplicateFrame = useCallback(() => {
    setTimelineFrames((prev) => {
      if (prev.length >= MAX_FRAMES || currentFrameIndex >= prev.length) return prev;
      const frame = prev[currentFrameIndex];
      const duplicated: TimelineFrame = {
        id: generateFrameId(),
        strokes: frame.strokes.map((s) => ({ ...s })),
        hold: frame.hold,
      };
      const updated = [...prev];
      updated.splice(currentFrameIndex + 1, 0, duplicated);
      setCurrentFrameIndex(currentFrameIndex + 1);
      return updated;
    });
  }, [currentFrameIndex]);

  const handleDeleteFrame = useCallback((index: number) => {
    setTimelineFrames((prev) => prev.filter((_, i) => i !== index));
    setCurrentFrameIndex((prevIdx) => (index < prevIdx ? prevIdx - 1 : prevIdx));
  }, []);

  const handleGoToFrame = useCallback((index: number) => {
    setCurrentFrameIndex(index);
  }, []);

  const handleReorderFrames = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setTimelineFrames((prev) => {
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
    setCurrentFrameIndex((prevIdx) => {
      if (fromIndex === prevIdx) return toIndex;
      if (fromIndex < prevIdx && toIndex >= prevIdx) return prevIdx - 1;
      if (fromIndex > prevIdx && toIndex <= prevIdx) return prevIdx + 1;
      return prevIdx;
    });
  }, []);

  const handleResizeFrame = useCallback((index: number, newHold: number) => {
    const clamped = Math.max(1, Math.min(MAX_HOLD, Math.round(newHold)));
    setTimelineFrames((prev) =>
      prev.map((f, i) => (i === index ? { ...f, hold: clamped } : f)),
    );
  }, []);

  const handleSetAnimationFps = useCallback((fps: number) => {
    setAnimationFps(Math.max(1, Math.min(MAX_FPS, fps)));
  }, []);

  const handleTogglePreview = useCallback(() => {
    setIsPreviewingAnimation((prev) => {
      if (!prev) setPreviewTick(0);
      return !prev;
    });
  }, []);

  const getAnimationData = useCallback((): AnimatedDrawing | undefined => submissionAnimation, [submissionAnimation]);

  const handleEraserToggle = useCallback(() => setIsEraser(true), []);
  const handleEraserOff = useCallback(() => setIsEraser(false), []);
  const handleMouseEnter = useCallback(() => setShowToolbar(true), []);
  const handleMouseLeave = useCallback(() => setShowToolbar(false), []);

  const allStrokes: Stroke[] = isAnimationMode
    ? strokes
    : [
        ...strokes,
        ...pendingStrokes.map((s, i) => ({
          id: `pending-${i}`,
          channelId,
          userId,
          createdAt: Date.now(),
          displayName: pendingDisplayName,
          ...s,
        })),
      ];

  const rawPendingAmount = calculateSubmissionBits(effectivePendingStrokes, submissionAnimation, 1920, 1080, config);
  const pendingAmount = getTierAmount(rawPendingAmount);

  return {
    appState,
    setAppState,
    strokes,
    setStrokes,
    pendingStrokes: effectivePendingStrokes,
    setPendingStrokes,
    currentStroke,
    totalAmount,
    setTotalAmount,
    color,
    setColor,
    width,
    setWidth,
    isEraser,
    showToolbar,
    displayDuration,
    pendingStrokesRef,
    handleStartStroke,
    handleContinueStroke,
    handleEndStroke,
    handleSubmit,
    handleCancelTransaction,
    handleClearPending,
    handleEraserToggle,
    handleEraserOff,
    handleMouseEnter,
    handleMouseLeave,
    allStrokes,
    pendingAmount,
    isAnimationMode,
    timelineFrames,
    currentFrameIndex,
    animationFps,
    isPreviewingAnimation,
    previewFrameIndex,
    setPreviewTick,
    onionSkinData,
    handleToggleAnimationMode,
    handleAddFrame,
    handleDuplicateFrame,
    handleDeleteFrame,
    handleGoToFrame,
    handleReorderFrames,
    handleResizeFrame,
    handleSetAnimationFps,
    handleTogglePreview,
    getAnimationData,
  };
}
